import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Email as EmailIcon,
  Info as InfoIcon,
  NotificationsActive as NotificationsActiveIcon,
  OpenInNew as OpenInNewIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SwitchField from '../common/SwitchField';
import { BRAND } from '../../theme/brandTokens';
import ProCrownButton from '../billing/ProCrownButton';
import ProLockedSection from '../billing/ProLockedSection';

const SENDER_NAME_MAX_LENGTH = 15;
const SENDER_NAME_ALLOWED_CHARS = /[^A-Za-z0-9 ]/g;

const sectionContentPadding = { xs: 2.5, sm: 3, md: 4 };

const innerSectionStyles = {
  p: { xs: 2, sm: 2.5 },
  backgroundColor: BRAND.surface,
  borderRadius: 2,
  border: `1px solid ${BRAND.line}`,
};

const AlwaysSentBadge = ({ label }) => (
  <Chip
    icon={<EmailIcon sx={{ fontSize: '0.85rem !important' }} />}
    label={label}
    size="small"
    sx={{
      backgroundColor: '#e8f5e9',
      color: '#2e7d32',
      fontWeight: 600,
      fontSize: '0.72rem',
      height: 24,
      '& .MuiChip-icon': { color: '#2e7d32' },
    }}
  />
);

const ChannelRow = ({ emailNode, smsNode, pushNode }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
    mt: 1.5,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <EmailIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
      {emailNode}
    </Box>
    {smsNode ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <SmsIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
        {smsNode}
      </Box>
    ) : null}
    {pushNode ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <NotificationsActiveIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
        {pushNode}
      </Box>
    ) : null}
  </Box>
);

const CommunicationNotificationsPanel = ({
  storeId,
  settings,
  smsBillingActive,
  smsUnitPrice = 0.03,
  smsCurrency = 'EUR',
  hasGoogleReviewUrl,
  senderName,
  senderNameError,
  defaultSenderName,
  saving,
  loading,
  isOwner,
  onSenderNameChange,
  onSenderNameErrorChange,
  onFieldChange,
  onSave,
  renderSmsToggle,
  allowsSms = true,
}) => {
  const { t, i18n } = useTranslation();
  const smsDisabledHelp = !smsBillingActive ? t('communicationSettingsPage.smsDisabledHelp') : '';
  const smsPriceLabel = new Intl.NumberFormat(
    i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'es' ? 'es-ES' : 'en-GB',
    { style: 'currency', currency: smsCurrency || 'EUR' }
  ).format(smsUnitPrice);
  const hasSmsEnabled = Boolean(
    settings?.confirmationSmsEnabled
    || settings?.cancellationSmsEnabled
    || settings?.reminder7DaySmsEnabled
    || settings?.reminder1DaySmsEnabled
    || settings?.reminder2HourSmsEnabled
    || settings?.googleReviewAskSmsEnabled
  );

  return (
    <Box>
      {allowsSms && !smsBillingActive ? (
        <Box sx={{ p: sectionContentPadding, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <InfoIcon sx={{ color: BRAND.accentText, mt: 0.25, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: isOwner ? 0.5 : 0 }}>
                {t('communicationSettingsPage.smsBillingBanner.text')}
              </Typography>
              {isOwner ? (
                <Typography
                  component={RouterLink}
                  to={`/management/stores/${storeId}/payment-settings`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: BRAND.accentText,
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('communicationSettingsPage.smsBillingBanner.link')}
                  <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>
      ) : null}
      {allowsSms && hasSmsEnabled ? (
        <Box sx={{ px: sectionContentPadding, pt: 1 }}>
          <Alert severity="warning">
            {t('communicationSettingsPage.smsPriceWarning', { price: smsPriceLabel })}
          </Alert>
        </Box>
      ) : null}

      <Box sx={{ p: sectionContentPadding }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CampaignIcon sx={{ color: BRAND.accentText }} />
          <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600 }}>
            {t('communicationSettingsPage.title')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('communicationSettingsPage.subtitle')}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2.5}>
          <ProLockedSection locked={!allowsSms}>
          <Box sx={innerSectionStyles}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SmsIcon sx={{ fontSize: '1.1rem', color: BRAND.accentText }} />
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {t('communicationSettingsPage.smsSenderName.label')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('communicationSettingsPage.smsSenderName.help')}
            </Typography>
            <TextField
              value={senderName}
              onChange={(e) => {
                const filtered = e.target.value.replace(SENDER_NAME_ALLOWED_CHARS, '');
                onSenderNameChange(filtered);
                onSenderNameErrorChange('');
              }}
              inputProps={{ maxLength: SENDER_NAME_MAX_LENGTH }}
              placeholder={defaultSenderName || t('communicationSettingsPage.smsSenderName.placeholder')}
              disabled={saving}
              error={Boolean(senderNameError)}
              helperText={senderNameError || `${senderName.length}/${SENDER_NAME_MAX_LENGTH}`}
              size="small"
              sx={{
                maxWidth: 260,
                '& .MuiOutlinedInput-root': {
                  height: 48,
                  borderRadius: '8px',
                  '& fieldset': { borderColor: BRAND.line, borderWidth: '2px' },
                  '&:hover fieldset': { borderColor: BRAND.accent },
                  '&.Mui-focused fieldset': { borderColor: BRAND.accent, borderWidth: '2px' },
                },
              }}
            />
          </Box>
          </ProLockedSection>

          <Box sx={innerSectionStyles}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('communicationSettingsPage.confirmation.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('communicationSettingsPage.confirmation.description')}
            </Typography>
            <ChannelRow
              emailNode={<AlwaysSentBadge label={t('communicationSettingsPage.emailAlways')} />}
              smsNode={renderSmsToggle('confirmationSmsEnabled')}
              pushNode={(
                <SwitchField
                  label={t('communicationSettingsPage.pushLabel')}
                  checked={Boolean(settings?.confirmationPushEnabled)}
                  onChange={(e) => onFieldChange('confirmationPushEnabled', e.target.checked)}
                  disabled={saving}
                  compact
                />
              )}
            />
          </Box>

          <Box sx={innerSectionStyles}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('communicationSettingsPage.cancellation.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('communicationSettingsPage.cancellation.description')}
            </Typography>
            <ChannelRow
              emailNode={<AlwaysSentBadge label={t('communicationSettingsPage.emailAlways')} />}
              smsNode={renderSmsToggle('cancellationSmsEnabled')}
              pushNode={(
                <SwitchField
                  label={t('communicationSettingsPage.pushLabel')}
                  checked={Boolean(settings?.cancellationPushEnabled)}
                  onChange={(e) => onFieldChange('cancellationPushEnabled', e.target.checked)}
                  disabled={saving}
                  compact
                />
              )}
            />
          </Box>

          <Box sx={innerSectionStyles}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('communicationSettingsPage.reminders.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('communicationSettingsPage.reminders.description')}
            </Typography>
            <Stack spacing={0} divider={<Divider />}>
              {[
                {
                  label: t('communicationSettingsPage.reminder7Day'),
                  emailField: 'reminder7DayEmailEnabled',
                  smsField: 'reminder7DaySmsEnabled',
                  pushField: 'reminder7DayPushEnabled',
                },
                {
                  label: t('communicationSettingsPage.reminder1Day'),
                  emailField: 'reminder1DayEmailEnabled',
                  smsField: 'reminder1DaySmsEnabled',
                  pushField: 'reminder1DayPushEnabled',
                },
                {
                  label: t('communicationSettingsPage.reminder2Hour'),
                  emailField: 'reminder2HourEmailEnabled',
                  smsField: 'reminder2HourSmsEnabled',
                  pushField: 'reminder2HourPushEnabled',
                },
              ].map(({ label, emailField, smsField, pushField }) => {
                const emailOn = Boolean(settings?.[emailField]);
                const smsDisabled = saving || !smsBillingActive || !emailOn;
                const smsTooltip = !smsBillingActive
                  ? smsDisabledHelp
                  : !emailOn
                    ? t('communicationSettingsPage.smsDependsOnEmail')
                    : '';
                return (
                  <Box
                    key={emailField}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      py: 1.25,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 110 }}>
                      {label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmailIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <SwitchField
                          label={t('communicationSettingsPage.emailLabel')}
                          checked={emailOn}
                          onChange={(e) => onFieldChange(emailField, e.target.checked)}
                          disabled={saving}
                          compact
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SmsIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        {allowsSms ? (
                        <Tooltip title={smsTooltip} placement="top" disableHoverListener={!smsTooltip}>
                          <span>
                            <SwitchField
                              label={t('communicationSettingsPage.smsLabel')}
                              checked={Boolean(settings?.[smsField])}
                              onChange={(e) => onFieldChange(smsField, e.target.checked)}
                              disabled={smsDisabled}
                              compact
                            />
                          </span>
                        </Tooltip>
                        ) : (
                          <SwitchField
                            label={(
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                {t('communicationSettingsPage.smsLabel')}
                                <ProCrownButton locked size="tiny" />
                              </Box>
                            )}
                            checked={false}
                            onChange={() => {}}
                            disabled
                            compact
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <NotificationsActiveIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <SwitchField
                          label={t('communicationSettingsPage.pushLabel')}
                          checked={Boolean(settings?.[pushField])}
                          onChange={(e) => onFieldChange(pushField, e.target.checked)}
                          disabled={saving}
                          compact
                        />
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          <Box sx={innerSectionStyles}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('communicationSettingsPage.googleReviewAsk.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('communicationSettingsPage.googleReviewAsk.description')}
            </Typography>
            {!hasGoogleReviewUrl ? (
              <Typography variant="body2" sx={{ color: BRAND.accentText, mb: 1 }}>
                {t('communicationSettingsPage.googleReviewAsk.missingUrl')}{' '}
                <Typography
                  component={RouterLink}
                  to={`/management/stores/${storeId}/edit`}
                  sx={{
                    color: BRAND.accentText,
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('communicationSettingsPage.googleReviewAsk.editStore')}
                </Typography>
              </Typography>
            ) : null}
            <ChannelRow
              emailNode={(
                <SwitchField
                  label={t('communicationSettingsPage.emailLabel')}
                  checked={Boolean(settings?.googleReviewAskEmailEnabled)}
                  onChange={(e) => onFieldChange('googleReviewAskEmailEnabled', e.target.checked)}
                  disabled={saving || (!hasGoogleReviewUrl && !settings?.googleReviewAskEmailEnabled)}
                  compact
                />
              )}
              smsNode={renderSmsToggle(
                'googleReviewAskSmsEnabled',
                !hasGoogleReviewUrl && !settings?.googleReviewAskSmsEnabled
              )}
              pushNode={(
                <SwitchField
                  label={t('communicationSettingsPage.pushLabel')}
                  checked={Boolean(settings?.googleReviewAskPushEnabled)}
                  onChange={(e) => onFieldChange('googleReviewAskPushEnabled', e.target.checked)}
                  disabled={saving || (!hasGoogleReviewUrl && !settings?.googleReviewAskPushEnabled)}
                  compact
                />
              )}
            />
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PrimaryActionButton
            onClick={onSave}
            disabled={saving || loading}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? t('common.saving') : t('common.save')}
          </PrimaryActionButton>
        </Box>
      </Box>
    </Box>
  );
};

export default CommunicationNotificationsPanel;
